<script setup lang="ts">
/**
 * HomeSearchWizard — parcours de recherche en étapes du hero de l'accueil
 * (initiative recherche-accueil ; visuel `ux-parcours-recherche/DESIGN.md`,
 * comportement `EXPERIENCE.md`). Rendu par HeroHomeV3 quand
 * `runtimeConfig.public.homeWizard` est vrai, à la place du sélecteur de type et
 * de la pill.
 *
 * Étapes : 1 (type), 2 (destination), 3 (dates), 4 (voyageurs), 5 (envies =
 * séjours types, facultative). Les dates partent selon l'option active : « Ce week-end »
 * (calculé au lancement, date locale), « Choisir mes dates » (calendrier
 * intégré `HoliHomeSearchWizardCalendar`) ou aucune date (« Je ne sais pas
 * encore »). Changer d'option garde les dates choisies en mémoire. L'étape 4
 * (variante grande de `HoliGuestCounter`, jamais d'âges d'enfants) LANCE la
 * recherche par « Voir les N maisons » ; « Préciser mes envies ou besoins » ouvre l'étape
 * 5 (`HoliSejourChips` 56 px, les 14 séjours types ; `table-dhotes` seulement
 * en Chambre d'hôtes), qui lance de même. L'étape 5 n'est jamais imposée.
 *
 * Les séjours types cochés font partie des réponses complètes dès l'étape 4
 * (retour depuis 5 compris) : même query pour le compteur et le lancement
 * (`sejours=a,b`). Une région ou un département garde sa page pSEO, qui lit
 * `sejours` (cf. `resolveHomeSearchRoute`). Les envies booléennes de `/search`
 * (`QUICK_FILTERS`) ne sont ni proposées, ni relues, ni envoyées.
 *
 * Compteur `/api/search/count` (`useSearchCount`) : sa query est dérivée de la
 * route que le lancement ouvrirait (`homeSearchCountQuery`), donc le chiffre
 * est celui de la page d'arrivée. Étapes 2-3, lieu retenu : on compte la
 * sortie anticipée (dates libres, 2 adultes, 0 enfant, aucun séjour type) que lance
 * le lien « Voir les N maisons sans préciser » ; étapes 4-5 : les réponses
 * complètes. À zéro (étapes 4-5), bouton désactivé + actions pour élargir
 * (« Retirer mes envies & besoins » en premier s'il y en a). Le libellé chiffré est
 * annoncé (`aria-live`) une fois par changement d'étape, jamais à chaque clic
 * d'un compteur ou d'une case.
 *
 * Règles tenues ici :
 * - une question à la fois, pas d'auto-avance : choisir ne change jamais
 *   d'écran, « Continuer » (ou Entrée) confirme ;
 * - « Continuer » n'est jamais désactivé : sans réponse, un message rouge
 *   annoncé poliment et relié au bouton (`aria-describedby`) ;
 * - étape 1 rendue côté serveur, rien de présélectionné sans `?type` ;
 * - une entrée d'historique par étape, SANS paramètre d'URL : `router.push`
 *   vers la même URL avec `state.homeWizardStep` et `force` (Vue Router refuse
 *   sinon une navigation identique). Le bouton Retour du navigateur déclenche
 *   `popstate`, on relit `history.state.homeWizardStep` (défaut 1) ;
 * - focus sur la question (H2 `tabindex="-1"`) à chaque changement d'étape,
 *   jamais au premier rendu ;
 * - question + panneau entrent en glissant de 28 px avec fondu (0,32 s), depuis
 *   la droite en avançant, depuis la gauche en revenant ; rien sous
 *   `prefers-reduced-motion`, ni au premier rendu, ni au préremplissage.
 *
 * Mémoire de session (`useSearchCriteria`, sessionStorage ; conversions pures
 * mémoire ↔ réponses dans `~/utils/home-wizard-memory`) :
 * - préremplissage au montage, client seulement (l'étape 1 reste celle du HTML
 *   serveur) : type (`?type` l'emporte ; type mémorisé sans `?type` coché ET
 *   posé dans l'URL), lieu (ville ; région par nom et département par code
 *   reconstruits via `REGIONS` ; « Partout en France » par le marqueur
 *   `everywhere`), dates (arrivée passée → retirées ; week-end qui vient → « Ce
 *   week-end » ; sinon « Choisir mes dates » ; « Je ne sais pas encore » par le
 *   marqueur `datesFree`), voyageurs, séjours types (`sejours` ; `table-dhotes`
 *   ignoré hors Chambre d'hôtes). Le parcours s'ouvre alors sur la
 *   dernière étape atteignable (au plus 4), sans focus, annonce ni animation ;
 * - écriture à chaque changement de réponse du visiteur, jamais au montage ;
 * - historique : aucune entrée d'étape n'existe sous l'étape restaurée
 *   (« plancher ») ; y revenir change l'étape et réécrit l'entrée courante, sans
 *   jamais quitter l'accueil.
 *
 * Mesure (PostHog, via `usePostHog` : consentement, zones exclues et absence
 * de clé y sont gérés) :
 * - `home_wizard_step_completed` (`step` 1-5, `step_name`, plus les propriétés
 *   de l'étape ; étape 5 : `sejours` (slugs) et `sejours_count`) quand le visiteur QUITTE une étape vers l'avant par une action
 *   réussie : « Continuer »/Entrée valide, « Préciser mes envies ou besoins »,
 *   lancement aux étapes 4-5. Rien au montage, au
 *   préremplissage, sur Retour/Suivant du navigateur, « Revenir en arrière », un
 *   clic d'étape faite, un « Continuer » refusé ni les actions de l'état zéro ;
 * - `search_performed` (`source: 'home_wizard'`, `from_step`, `early_exit`,
 *   `sejours_count`) à
 *   chaque lancement qui navigue, sur les critères réellement lancés ;
 * - seulement des catégories et des nombres : jamais de nom de lieu, de texte
 *   saisi, de date calendaire, d'âge d'enfant ni d'URL.
 *
 * Disposition : ≥ md, grille à deux colonnes — à gauche question, réponses et,
 * à zéro aux étapes 4-5, les suggestions pour élargir (encart) ; à droite les
 * étapes empilées puis toutes les actions (« Continuer » / « Voir les x
 * maisons », envies, sortie anticipée, retour). < md : barre compacte,
 * question, suggestions, puis actions. Réponses en une colonne et calendrier
 * sur un seul mois, faute de largeur à côté des étapes.
 */
import type { SearchSuggestion } from '~/composables/useSearchSuggestions'
import type { HomeType } from '~/composables/useHomeType'
import { useSearchCriteria } from '~/composables/useSearchCriteria'
import { SEARCH_FILTERS } from '~~/shared/search-constants'
import { SEJOUR_SLUGS, type SejourSlug } from '~~/shared/establishment-filters'
import { pseoTypeFromHomeType } from '~/utils/pseo-guest-query'
import { homeSearchCountQuery, resolveHomeSearchRoute, type HomeSearchCriteria } from '~/utils/home-search-route'
import {
  answersFromMemory,
  datesOfAnswer,
  memoryFromAnswers,
  type DateRange,
  type WhenOption,
  type WizardPlace,
} from '~/utils/home-wizard-memory'
import { useSearchCount } from '~/composables/useSearchCount'
import {
  formatDayLong,
  formatShortRange,
  nightsBetween,
  toIsoDate,
  upcomingWeekend,
  type WizardDateRange,
} from '~/utils/wizard-dates'

export interface HomeSearchWizardRegion {
  slug: string
  name: string
  cdhCount: number
  giteCount: number
}

const props = withDefaults(defineProps<{
  /** Régions triées par densité du type actif (`index.vue`) ; 6 premières en chips. */
  regions?: HomeSearchWizardRegion[]
  /** Total des maisons du type actif (« Partout en France »). */
  totalCount?: number
}>(), {
  regions: () => [],
  totalCount: 0,
})

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const localePath = useLocalePath()
const { type: homeType, setType } = useHomeType()

// ─── Constantes ───
const TYPE_OPTIONS: HomeType[] = ['cdh', 'gite', 'all']
const WHEN_OPTIONS: WhenOption[] = ['weekend', 'pick', 'unknown']

const TYPE_SELECTED_CLASS: Record<HomeType, string> = {
  cdh: 'bg-type-cdh border-type-cdh text-paper-50',
  gite: 'bg-type-gite border-type-gite text-paper-900',
  all: 'bg-paper-900 border-paper-900 text-paper-50',
}
const TYPE_TICK_CLASS: Record<HomeType, string> = {
  cdh: 'text-type-cdh',
  gite: 'text-type-gite',
  all: 'text-paper-900',
}
const ZERO_ACTION_CLASS = 'inline-flex min-h-[52px] max-w-full cursor-pointer items-center rounded-lg border-[1.5px] border-sage-500 bg-paper-100 px-5 py-2 text-center text-lg font-medium leading-tight text-paper-900 [overflow-wrap:anywhere] hover:bg-paper-200/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700'

const questionId = useId()
const hintId = useId()
const errorId = useId()

// ─── État ───
const step = ref(1)
// Rien de coché sans `?type` (useHomeType rend « all » si absent ou invalide).
const selectedType = ref<HomeType | null>(homeType.value === 'all' ? null : homeType.value)
const place = ref<WizardPlace | null>(null)
const query = ref('')
const error = ref('')
const when = ref<WhenOption | null>(null)
const picked = ref<WizardDateRange>({ start: null, end: null })
/** « Aujourd'hui » (date locale), relu à chaque ouverture des étapes 3 à 5. */
const now = ref(new Date())
// Étape 4 : adultes 1-100, enfants 0-20 (bornes de `SEARCH_FILTERS`). Les âges
// des enfants ne sont jamais demandés ni transmis.
const {
  adults,
  children,
  maxAdults,
  maxChildren,
  setAdults,
  setChildren,
} = useGuestCounters({ minAdults: 1 })

// Étape 5 : séjours types cochés (ordre de `SEJOUR_SLUGS`, émis par `HoliSejourChips`).
const sejours = ref<SejourSlug[]>([])

const questionRef = ref<HTMLElement | null>(null)
const autocompleteRef = ref<{ suggestions: readonly SearchSuggestion[] } | null>(null)
const submitRef = ref<HTMLButtonElement | null>(null)
const zeroActionsRef = ref<HTMLElement | null>(null)
const otherRegionRef = ref<HTMLButtonElement | null>(null)

// `?type` changé hors du parcours (bouton Suivant du navigateur, lien typé) :
// la carte cochée suit. « Tout » ne coche rien tant qu'aucune réponse n'a été donnée.
watch(homeType, (next) => {
  if (next === selectedType.value) return
  if (selectedType.value === null && next === 'all') return
  selectedType.value = next
})

/**
 * Validation de l'étape 3 : clé du message d'erreur, ou `null` si les dates
 * sont valides (option choisie, et les deux jours pour « Choisir mes dates »).
 */
function datesErrorKey(): string | null {
  if (!when.value) return 'pages.home.wizard.errors.required'
  if (when.value === 'pick' && !picked.value.start) return 'pages.home.wizard.errors.pickArrival'
  if (when.value === 'pick' && !picked.value.end) return 'pages.home.wizard.errors.pickDeparture'
  return null
}

/** Dernière étape atteignable avec les réponses données. */
const lastReachableStep = computed(() => {
  if (!selectedType.value) return 1
  if (!place.value) return 2
  return datesErrorKey() === null ? 5 : 3
})

// ─── Navigation entre étapes ───
// Navigation `?type` en cours : l'entrée d'étape suivante l'attend, sinon son
// `push` annulerait le `replace` et le type ne serait jamais posé.
let pendingTypeNavigation: Promise<unknown> | null = null

async function goForward(next: number) {
  if (next >= 3) now.value = new Date()
  step.value = next
  error.value = ''
  if (pendingTypeNavigation) await pendingTypeNavigation
  await router.push({
    path: route.path,
    query: route.query,
    hash: route.hash,
    state: { homeWizardStep: next },
    force: true,
  })
}

/**
 * Plus basse étape dont une entrée d'historique existe dans ce passage : 1, ou
 * l'étape restaurée par le préremplissage (aucune entrée en dessous).
 */
let floorStep = 1
/** Retour sous le plancher depuis une étape plus haute : étape visée au `popstate`. */
let pendingBackTarget: number | null = null
/**
 * `router.go` lancé, `popstate` pas encore reçu : une seconde activation
 * (double clic) remonterait plus loin et pourrait quitter l'accueil.
 */
let historyTraversalPending = false

/** Étape changée sans entrée d'historique : l'entrée courante la porte désormais. */
function replaceStepHere(target: number) {
  step.value = target
  floorStep = Math.min(floorStep, target)
  window.history.replaceState({ ...window.history.state, homeWizardStep: target }, '')
}

/** « Revenir en arrière » ou clic sur une étape faite. */
function goBackTo(target: number) {
  const k = step.value - target
  if (k <= 0 || historyTraversalPending) return
  error.value = ''
  // Entrée courante inconnue : changement direct.
  if (window.history.state?.homeWizardStep !== step.value) {
    step.value = target
    return
  }
  // L'entrée de l'étape visée existe : on remonte l'historique, le `popstate`
  // remettra l'étape.
  if (target >= floorStep) {
    historyTraversalPending = true
    router.go(-k)
    return
  }
  // Sous le plancher (après un préremplissage) : aucune entrée à rejoindre, et
  // remonter plus loin quitterait l'accueil. Au plancher, changement direct ;
  // au-dessus, on redescend jusqu'à l'entrée du plancher, qui portera la cible.
  if (step.value === floorStep) {
    replaceStepHere(target)
    return
  }
  pendingBackTarget = target
  historyTraversalPending = true
  router.go(-(step.value - floorStep))
}

function onPopState() {
  historyTraversalPending = false
  if (pendingBackTarget !== null) {
    const target = pendingBackTarget
    pendingBackTarget = null
    error.value = ''
    replaceStepHere(target)
    return
  }
  const raw = Number(window.history.state?.homeWizardStep)
  const wanted = Number.isInteger(raw) && raw >= 1 ? raw : 1
  step.value = Math.min(wanted, lastReachableStep.value)
  // Retour sur les étapes 3 à 5 par le bouton Suivant : « aujourd'hui » relu.
  if (step.value >= 3) now.value = new Date()
  error.value = ''
}

onMounted(() => {
  // Entrée d'arrivée déjà estampillée (Retour depuis les résultats, rechargement) :
  // les entrées d'étapes de ce passage sont dessous, le plancher reste à 1.
  const arrivedOnStepEntry = window.history.state?.homeWizardStep !== undefined
  prefillFromMemory(arrivedOnStepEntry)
  // Entrée courante héritée d'un passage précédent (retour depuis les résultats,
  // rechargement) qui porte une autre étape : on la réaligne sur l'étape affichée,
  // sinon le prochain Retour tomberait sur cette entrée et demanderait deux appuis.
  // Étape restaurée : l'entrée la porte toujours (Retour depuis l'étape
  // suivante y ramène). Les clés de Vue Router (back, current, position…) sont
  // conservées.
  const state = window.history.state
  const known = state?.homeWizardStep !== undefined
  if ((known || step.value > 1) && state?.homeWizardStep !== step.value) {
    window.history.replaceState({ ...state, homeWizardStep: step.value }, '')
  }
  window.addEventListener('popstate', onPopState)
  // Écriture de la mémoire armée APRÈS le préremplissage : jamais au montage.
  watch(answersSnapshot, persistAnswers)
})
onBeforeUnmount(() => window.removeEventListener('popstate', onPopState))

// ─── Étape 1 · Type ───
function selectType(next: HomeType) {
  selectedType.value = next
  error.value = ''
  pendingTypeNavigation = setType(next)
}

// « À table avec les hôtes » n'a de sens qu'en chambre d'hôtes : retiré des
// séjours dès qu'un autre type est choisi (sinon il partirait dans l'URL sans
// être proposé).
watch(selectedType, (next) => {
  if (next !== 'cdh' && sejours.value.includes('table-dhotes')) {
    sejours.value = sejours.value.filter(slug => slug !== 'table-dhotes')
  }
})

const typeOptions = computed(() =>
  TYPE_OPTIONS.map(value => ({
    value,
    title: t(`pages.home.wizard.type.options.${value}.title`),
    description: t(`pages.home.wizard.type.options.${value}.description`),
    checked: selectedType.value === value,
  })),
)

// ─── Étape 2 · Destination ───
function countFor(p: { type: string; cdh?: number; gite?: number }): number {
  if (p.type === 'france') return props.totalCount
  const kind = selectedType.value ?? 'all'
  if (kind === 'cdh') return p.cdh ?? 0
  if (kind === 'gite') return p.gite ?? 0
  return (p.cdh ?? 0) + (p.gite ?? 0)
}

const regionChips = computed(() => {
  const chips: { id: string; place: WizardPlace }[] = props.regions.slice(0, 6).map(r => ({
    id: `region-${r.slug}`,
    place: { type: 'region', value: r.name, slug: r.slug, cdh: r.cdhCount, gite: r.giteCount },
  }))
  chips.push({ id: 'france', place: { type: 'france', value: t('pages.home.wizard.destination.everywhere') } })
  return chips
})

function isChipChecked(chip: WizardPlace): boolean {
  const current = place.value
  if (!current || current.type !== chip.type) return false
  return chip.type === 'france' || current.slug === chip.slug
}

function selectChip(chip: WizardPlace) {
  place.value = chip
  // Choisir une chip vide le champ.
  query.value = ''
  error.value = ''
}

function selectSuggestion(suggestion: SearchSuggestion) {
  place.value = { ...suggestion, cdh: suggestion.count, gite: suggestion.giteCount }
  query.value = suggestion.value
  error.value = ''
}

/** Saisie dans le champ : taper efface la chip ou la suggestion choisie. */
function onQueryInput(value: string) {
  // Écho de nos propres écritures (sélection, chip) : rien à effacer.
  if (value === query.value) return
  query.value = value
  place.value = null
}

/** Minuscules sans accents (diacritiques combinants U+0300 à U+036F retirés). */
function normalize(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
}

const noMatchMessage = computed(() =>
  t('pages.home.wizard.destination.noMatch', { query: query.value.trim() }),
)

const placeLine = computed(() => {
  const p = place.value
  if (!p) return ''
  const parts = [p.value]
  if (p.type !== 'france') parts.push(t(`pages.home.wizard.kindInline.${p.type}`))
  const n = countFor(p)
  if (n > 0) parts.push(t('pages.home.wizard.houses', { count: n }, n))
  return parts.join(' · ')
})

// ─── Étape 3 · Dates ───
const todayIso = computed(() => toIsoDate(now.value))
const weekend = computed(() => upcomingWeekend(now.value))

/** « Ce week-end » vu de maintenant : lancement, mesure et mémoire le recalculent. */
function weekendNow(): DateRange {
  return upcomingWeekend(new Date())
}

const whenOptions = computed(() =>
  WHEN_OPTIONS.map(value => ({
    value,
    label: t(`pages.home.wizard.dates.options.${value}`),
    checked: when.value === value,
  })),
)

function selectWhen(next: WhenOption) {
  when.value = next
  error.value = ''
}

function onPickedUpdate(next: WizardDateRange) {
  picked.value = next
  error.value = ''
}

/** « Du vendredi 23 au dimanche 25 octobre, soit 2 nuits. » */
const weekendText = computed(() => {
  const { start, end } = weekend.value
  const sameMonth = start.slice(0, 7) === end.slice(0, 7)
  const nights = nightsBetween(start, end)
  return t('pages.home.wizard.dates.weekendText', {
    start: formatDayLong(start, locale.value, { month: !sameMonth }),
    end: formatDayLong(end, locale.value),
    nights: t('pages.home.wizard.dates.nights', { count: nights }, nights),
  })
})

/**
 * Dates de l'option active. Le compteur passe le week-end de « aujourd'hui »
 * (relu à l'ouverture des étapes 3 à 5) ; le lancement le recalcule maintenant.
 */
function selectedDates(weekendRange: DateRange): DateRange | null {
  return datesOfAnswer(when.value, picked.value, weekendRange)
}

/** Récapitulatif de l'étape Dates dans la barre d'étapes. */
const datesSummary = computed(() => {
  if (when.value === 'weekend') {
    const { start, end } = weekend.value
    return t('pages.home.wizard.dates.summary.weekend', { range: formatShortRange(start, end, locale.value) })
  }
  if (when.value === 'pick') {
    const { start, end } = picked.value
    return start && end ? formatShortRange(start, end, locale.value) : ''
  }
  if (when.value === 'unknown') return t('pages.home.wizard.dates.summary.free')
  return ''
})

// ─── Étape 4 · Voyageurs ───
/** « 2 adultes », « 4 adultes, 3 enfants ». */
const guestsSummary = computed(() => {
  const a = t('pages.home.wizard.guests.summary.adults', { count: adults.value }, adults.value)
  if (children.value <= 0) return a
  const c = t('pages.home.wizard.guests.summary.children', { count: children.value }, children.value)
  return t('pages.home.wizard.guests.summary.both', { adults: a, children: c })
})

const guestRows = computed(() => [
  {
    key: 'adults',
    value: adults.value,
    min: 1,
    max: maxAdults,
    set: setAdults,
  },
  {
    key: 'children',
    value: children.value,
    min: 0,
    max: maxChildren,
    set: setChildren,
  },
].map(row => ({
  ...row,
  label: t(`pages.home.wizard.guests.${row.key}.label`),
  sublabel: t(`pages.home.wizard.guests.${row.key}.sublabel`),
  inputLabel: t(`pages.home.wizard.guests.${row.key}.input`),
  decrementLabel: t(`pages.home.wizard.guests.${row.key}.decrement`),
  incrementLabel: t(`pages.home.wizard.guests.${row.key}.increment`),
})))

// ─── Étape 5 · Envies ───
/** Type API du parcours pour `HoliSejourChips` (`table-dhotes` en chambre d'hôtes seulement). */
const sejoursType = computed(() => pseoTypeFromHomeType(selectedType.value ?? 'all'))

function onSejoursUpdate(next: SejourSlug[]) {
  sejours.value = next
}

/** « Préciser mes envies ou besoins » (étape 4) : ouvre l'étape 5. */
function openEnvies() {
  captureStep(4)
  void goForward(5)
}

// ─── Barre d'étapes ───
const progressSteps = computed(() => [
  {
    name: t('pages.home.wizard.steps.type'),
    icon: 'i-lucide-house',
    value: selectedType.value ? t(`pages.home.wizard.type.options.${selectedType.value}.title`) : '',
  },
  { name: t('pages.home.wizard.steps.destination'), icon: 'i-lucide-map-pin', value: place.value?.value ?? '' },
  { name: t('pages.home.wizard.steps.dates'), icon: 'i-lucide-calendar', value: datesSummary.value },
  { name: t('pages.home.wizard.steps.guests'), icon: 'i-lucide-users', value: guestsSummary.value },
  { name: t('pages.home.wizard.steps.envies'), icon: 'i-lucide-sparkles', optional: true },
])

const STEP_KEYS = ['type', 'destination', 'dates', 'guests', 'envies'] as const
const stepKey = computed(() => STEP_KEYS[step.value - 1] ?? 'type')
const question = computed(() => t(`pages.home.wizard.${stepKey.value}.question`))
const hint = computed(() => t(`pages.home.wizard.${stepKey.value}.hint`))

// ─── Critères, compteur et lancement ───
/** Sortie anticipée (étapes 2-3) : dates libres, voyageurs par défaut. */
const EARLY_CRITERIA: HomeSearchCriteria = {
  dates: null,
  adults: SEARCH_FILTERS.DEFAULT_ADULTS,
  children: SEARCH_FILTERS.DEFAULT_CHILDREN,
}

/** Réponses complètes des étapes 4-5 (jamais d'âges d'enfants), séjours types compris. */
function fullCriteria(weekendRange: DateRange): HomeSearchCriteria {
  return {
    dates: selectedDates(weekendRange),
    adults: adults.value,
    children: children.value,
    sejours: [...sejours.value],
  }
}

function locationOf(target: WizardPlace) {
  return target.type === 'france' ? null : target
}

const countQuery = computed(() => {
  if (step.value < 2 || !place.value) return null
  const criteria = step.value >= 4 ? fullCriteria(weekend.value) : EARLY_CRITERIA
  return homeSearchCountQuery(locationOf(place.value), '', selectedType.value ?? 'all', criteria)
})
const {
  total: countTotal,
  expandedRadiusKm,
  pending: countPending,
  error: countError,
} = useSearchCount(countQuery)

const isZero = computed(() => countQuery.value !== null && !countError.value && countTotal.value === 0)
/** Étapes 4-5 à zéro : bouton désactivé, Entrée ne lance rien. */
const launchBlocked = computed(() => step.value >= 4 && isZero.value)

/** Libellé du bouton de lancement (étapes 4-5) : « Voir les 12 maisons », zone élargie, ou sans chiffre. */
const launchLabel = computed(() => {
  const n = countTotal.value
  if (countError.value || n == null) return t('pages.home.wizard.count.generic')
  if (n === 0) return t('pages.home.wizard.zero.button')
  return expandedRadiusKm.value
    ? t('pages.home.wizard.count.showExpanded', { count: n, radius: expandedRadiusKm.value }, n)
    : t('pages.home.wizard.count.show', { count: n }, n)
})

/** Lien de sortie anticipée (étapes 2-3) : « Voir les 12 maisons sans préciser ». */
const earlyLabel = computed(() => {
  const n = countTotal.value
  if (countError.value || n == null) return t('pages.home.wizard.count.earlyGeneric')
  return expandedRadiusKm.value
    ? t('pages.home.wizard.count.earlyExpanded', { count: n, radius: expandedRadiusKm.value }, n)
    : t('pages.home.wizard.count.early', { count: n }, n)
})
/** Masqué sans lieu et à zéro (le lien ouvrirait une page vide). */
const showEarlyExit = computed(() => (step.value === 2 || step.value === 3) && !!place.value && !isZero.value)

// Annonce polie : une fois le compte arrivé après un changement d'étape (2 à
// 5), jamais à chaque clic d'un compteur ou d'une envie (le drapeau ne se
// relève qu'au changement d'étape).
const liveMessage = ref('')
let announcePending = false
// Espace insécable alternée en fin de libellé : quand l'étape change sans que
// la recherche change, le libellé est le même qu'à l'annonce précédente, et un
// lecteur d'écran peut ignorer une région live réécrite à l'identique.
let announceSuffix = ''

function announce(message: string) {
  announceSuffix = announceSuffix ? '' : '\u00A0'
  liveMessage.value = message + announceSuffix
}

function tryAnnounce() {
  if (!announcePending || countQuery.value === null || countPending.value) return
  // Erreur : rien à annoncer (aucun chiffre) ; pas encore de chiffre : on attend.
  if (countError.value) {
    announcePending = false
    return
  }
  if (countTotal.value == null) return
  announcePending = false
  if (step.value >= 4) announce(launchLabel.value)
  // Étapes 2-3 : on n'annonce que le lien affiché (masqué à zéro).
  else if (showEarlyExit.value) announce(earlyLabel.value)
}

// ─── Changement d'étape : transition, focus, annonce ───
/** Étape posée par le préremplissage : ni focus, ni annonce, ni animation. */
let silentStepChange = false
/** Nom de la `<Transition>` entre étapes ; vide = aucune animation. */
const stepTransition = ref('')

// Flush `pre` : le nom est posé avant le rendu qui change la clé de l'étape.
watch(step, (next, prev) => {
  liveMessage.value = ''
  if (silentStepChange) {
    silentStepChange = false
    stepTransition.value = ''
    announcePending = false
    return
  }
  stepTransition.value = next > prev ? 'wizard-step-forward' : 'wizard-step-back'
  // Focus sur la question à chaque changement d'étape (jamais au premier rendu).
  void nextTick(() => questionRef.value?.focus())
  announcePending = next >= 2 && next <= 5
  // Après le recalcul de la query : sans nouvelle requête (query identique), le
  // chiffre courant est le bon ; sinon on attend la fin du calcul.
  void nextTick(tryAnnounce)
})

// ─── Bouton principal collé en bas (< md) ───
/**
 * Champ texte focalisé (destination) : clavier virtuel ouvert, la liste de
 * suggestions occupe l'espace jusqu'au clavier ; le bouton ne colle pas.
 */
const textFieldFocused = ref(false)
function isTextField(target: EventTarget | null) {
  return target instanceof HTMLInputElement && !['radio', 'checkbox', 'button', 'submit'].includes(target.type)
}
function onFormFocusIn(event: FocusEvent) {
  textFieldFocused.value = isTextField(event.target)
}
// Focus perdu sans destination (clavier refermé, suggestion non focalisable
// touchée) : la liste reste ouverte, le bouton reste décollé jusqu'au prochain
// focus dans le parcours.
function onFormFocusOut(event: FocusEvent) {
  if (event.relatedTarget && !isTextField(event.relatedTarget)) textFieldFocused.value = false
}

/**
 * Bouton réellement collé : les actions qui le suivent sont sous l'écran.
 * Il porte alors `data-bottom-bar`, que `UiFloatingActions` mesure pour
 * remonter « Haut de page » au-dessus de lui au lieu de le recouvrir.
 */
const actionsRef = ref<HTMLElement | null>(null)
const submitStuck = ref(false)
let stuckObserver: IntersectionObserver | null = null
onMounted(() => {
  if (!actionsRef.value || typeof IntersectionObserver === 'undefined') return
  const mobile = window.matchMedia('(max-width: 767px)')
  stuckObserver = new IntersectionObserver(([entry]) => {
    submitStuck.value = !!entry && mobile.matches && !entry.isIntersecting
      && entry.boundingClientRect.top > (entry.rootBounds?.bottom ?? window.innerHeight)
  })
  stuckObserver.observe(actionsRef.value)
})
onBeforeUnmount(() => stuckObserver?.disconnect())

/** L'ancienne étape disparaît aussitôt (pas de double hauteur pendant l'entrée). */
function onStepLeave(_el: Element, done: () => void) {
  done()
}

// Pendant le glissement, le parcours rogne l'axe horizontal : les 28 px de
// départ ne font pas apparaître de défilement horizontal sur mobile.
const stepAnimating = ref(false)
function onStepBeforeEnter() {
  stepAnimating.value = stepTransition.value !== ''
}
function onStepEnterDone() {
  stepAnimating.value = false
}
watch(countPending, (isPending) => {
  if (!isPending) tryAnnounce()
})

// ─── État zéro : actions pour élargir ───
// Zéro → chiffre : le bloc d'actions disparaît ; s'il portait le focus, il
// passe sur le bouton de lancement redevenu actif.
watch(isZero, (zero, wasZero) => {
  if (zero || !wasZero || step.value < 4) return
  const active = typeof document === 'undefined' ? null : document.activeElement
  if (active && zeroActionsRef.value?.contains(active)) {
    void nextTick(() => submitRef.value?.focus())
  }
})

/**
 * « Retirer mes envies & besoins » : séjours types vidés, l'action disparaît, le focus passe sur
 * la première action restante (puis sur le bouton de lancement si le zéro
 * disparaît, cf. `watch(isZero)`).
 */
function removeEnvies() {
  sejours.value = []
  error.value = ''
  void nextTick(() => {
    zeroActionsRef.value?.querySelector<HTMLButtonElement>('[data-zero-action]')?.focus()
  })
}

/** « Dates libres » : l'action disparaît, le focus passe sur « Choisir une autre région ». */
function freeDates() {
  when.value = 'unknown'
  error.value = ''
  void nextTick(() => otherRegionRef.value?.focus())
}

/** « Choisir une autre région » : lieu et champ vidés, retour à l'étape 2. */
function chooseOtherRegion() {
  place.value = null
  query.value = ''
  goBackTo(2)
}

// ─── Mesure (PostHog) ───
const { capture } = usePostHog()

type LocationType = 'region' | 'department' | 'city' | 'tourist_site' | 'france'
const LOCATION_TYPES: readonly string[] = ['region', 'department', 'city', 'tourist_site', 'france']

/** Catégorie du lieu retenu (jamais son nom) ; `null` hors vocabulaire. */
function locationTypeOf(target: WizardPlace | null): LocationType | null {
  return target && LOCATION_TYPES.includes(target.type) ? target.type as LocationType : null
}

/** Nuits de la plage, même résultat que le calcul de la pill (`useHomeSearch`) ; 0 sans date. */
function nightsIn(dates: DateRange | null | undefined): number {
  return dates ? nightsBetween(dates.start, dates.end) : 0
}

/** Propriétés propres à l'étape franchie (catégories et nombres seulement). */
function stepProperties(n: number): Record<string, unknown> {
  switch (n) {
    case 1:
      return { type: selectedType.value ?? 'all' }
    case 2:
      return { location_type: locationTypeOf(place.value) }
    case 3:
      return { dates_option: when.value, nights: nightsIn(selectedDates(weekendNow())) }
    case 4:
      return { adults: adults.value, children: children.value }
    default: {
      const slugs = [...sejours.value]
      return { sejours: slugs, sejours_count: slugs.length }
    }
  }
}

/** Étape `n` franchie vers l'avant par une action réussie du visiteur. */
function captureStep(n: number, extra: Record<string, unknown> = {}) {
  capture('home_wizard_step_completed', {
    step: n,
    step_name: STEP_KEYS[n - 1],
    ...stepProperties(n),
    ...extra,
  })
}

/** Recherche lancée depuis le parcours, sur les critères réellement envoyés. */
function captureSearch(criteria: HomeSearchCriteria, fromStep: number, earlyExit: boolean) {
  capture('search_performed', {
    source: 'home_wizard',
    from_step: fromStep,
    early_exit: earlyExit,
    type: selectedType.value ?? 'all',
    location_type: locationTypeOf(place.value),
    has_dates: !!criteria.dates,
    nights: nightsIn(criteria.dates),
    adults: criteria.adults,
    children: criteria.children,
    sejours_count: criteria.sejours?.length ?? 0,
  })
}

// ─── Lancement ───
/**
 * Navigation de lancement en cours : une seconde activation (double clic,
 * Entrée répétée) ne relance ni la capture ni la navigation.
 */
let launchPending = false

/**
 * Lancement : seulement avec un lieu retenu. Hors sortie anticipée, l'étape
 * courante (4 ou 5) est franchie avant la recherche.
 */
function launch(criteria: HomeSearchCriteria, fromStep: number, earlyExit: boolean) {
  if (!place.value || launchPending) return
  const { path, query: searchQuery } = resolveHomeSearchRoute(
    locationOf(place.value),
    '',
    selectedType.value ?? 'all',
    criteria,
  )
  launchPending = true
  if (!earlyExit) captureStep(fromStep)
  captureSearch(criteria, fromStep, earlyExit)
  void Promise.resolve(router.push({ path: localePath(path), query: searchQuery }))
    .catch(() => {})
    .finally(() => {
      launchPending = false
    })
}

function launchEarly() {
  launch(EARLY_CRITERIA, step.value, true)
}

// ─── Mémoire de session ───
const { load: loadCriteria, save: saveCriteria } = useSearchCriteria()

/**
 * Préremplissage depuis la mémoire (au montage, client seulement), puis
 * ouverture sur la dernière étape atteignable, au plus 4, sans focus, annonce
 * ni animation.
 */
function prefillFromMemory(arrivedOnStepEntry: boolean) {
  const memory = loadCriteria()
  if (!memory || typeof memory !== 'object') return

  const remembered = answersFromMemory(memory, {
    today: new Date(),
    regions: props.regions,
    everywhereLabel: t('pages.home.wizard.destination.everywhere'),
  })
  let memoryTypeApplied = false
  // `?type` dans l'URL l'emporte ; sinon le type mémorisé est coché ET posé dans l'URL.
  if (homeType.value === 'all' && remembered.type) {
    selectedType.value = remembered.type
    memoryTypeApplied = true
    if (remembered.type !== 'all') pendingTypeNavigation = setType(remembered.type)
  }

  if (remembered.place) {
    place.value = remembered.place
    // Le champ montre le lieu, sauf pour une chip (choisir une chip vide le champ).
    const isChip = regionChips.value.some(chip => isChipChecked(chip.place))
    query.value = isChip ? '' : remembered.place.value
  }

  if (remembered.when) when.value = remembered.when
  if (remembered.picked) picked.value = remembered.picked
  setAdults(remembered.adults)
  setChildren(remembered.children)

  if (remembered.sejours) {
    // Ordre de `SEJOUR_SLUGS` (comme au clic) ; `table-dhotes` ignoré hors Chambre d'hôtes.
    const kept = remembered.sejours
    sejours.value = SEJOUR_SLUGS.filter(slug =>
      kept.includes(slug) && (slug !== 'table-dhotes' || selectedType.value === 'cdh'))
  }

  // Saut d'étape seulement si la mémoire a fourni le type ou le lieu : un
  // `?type` d'URL avec une mémoire sans rapport (voyageurs de l'overlay) reste
  // sur l'étape 1, comme sans mémoire.
  if (!memoryTypeApplied && remembered.place === null) return
  const target = Math.min(lastReachableStep.value, 4)
  if (target > step.value) {
    if (target >= 3) now.value = new Date()
    silentStepChange = true
    step.value = target
    // Nouvelle entrée (lien du logo) : aucune entrée d'étape dessous.
    if (!arrivedOnStepEntry) floorStep = target
  }
}

/** Réponses du visiteur, sérialisées : la mémoire n'est écrite que si elles changent. */
function answersSnapshot(): string {
  return JSON.stringify([
    selectedType.value,
    place.value,
    when.value,
    picked.value.start,
    picked.value.end,
    adults.value,
    children.value,
    sejours.value,
  ])
}

/** Écrit les réponses en cours (dates de l'option active ; jamais d'âges d'enfants). */
function persistAnswers() {
  saveCriteria(memoryFromAnswers({
    type: selectedType.value,
    place: place.value,
    when: when.value,
    picked: picked.value,
    adults: adults.value,
    children: children.value,
    sejours: sejours.value,
  }, weekendNow()))
}

// ─── Continuer ───
function onContinue() {
  if (step.value === 1) {
    if (!selectedType.value) {
      error.value = t('pages.home.wizard.errors.required')
      return
    }
    captureStep(1)
    void goForward(2)
    return
  }

  if (step.value === 3) {
    const key = datesErrorKey()
    if (key) {
      error.value = t(key)
      return
    }
    captureStep(3)
    void goForward(4)
    return
  }

  if (step.value >= 4) {
    // Zéro : bouton désactivé, Entrée ne lance rien.
    if (launchBlocked.value) return
    launch(fullCriteria(weekendNow()), step.value, false)
    return
  }

  // Étape 2 : lieu retenu → étape 3.
  if (place.value) {
    captureStep(2)
    void goForward(3)
    return
  }
  const typed = normalize(query.value)
  if (!typed) {
    error.value = t('pages.home.wizard.errors.required')
    return
  }
  // Correspondance exacte avec une suggestion affichée : retenue automatiquement.
  const exact = autocompleteRef.value?.suggestions.find(s => normalize(s.value) === typed)
  if (exact) {
    selectSuggestion(exact)
    captureStep(2)
    void goForward(3)
    return
  }
  error.value = t('pages.home.wizard.errors.pickPlace')
}
</script>

<template>
  <form
    role="search"
    :aria-label="$t('pages.home.wizard.ariaLabel')"
    class="flex min-w-0 flex-col gap-6 font-sans text-lg leading-normal text-paper-900 md:grid md:grid-cols-[minmax(0,1fr)_14rem] md:grid-rows-[auto_auto_1fr] md:gap-x-10 md:gap-y-7"
    :class="stepAnimating ? 'overflow-x-clip' : ''"
    novalidate
    @submit.prevent="onContinue"
    @focusin="onFormFocusIn"
    @focusout="onFormFocusOut"
  >
    <HoliHomeSearchWizardProgress
      :steps="progressSteps"
      :current="step"
      class="md:col-start-2 md:row-start-1"
      @go="goBackTo"
    >
      <!-- < md : le retour monte sur la ligne « Étape n sur 5 », pour libérer le bas. -->
      <template #mobile-action>
        <button
          v-if="step > 1"
          type="button"
          data-back-mobile
          class="inline-flex min-h-11 flex-none cursor-pointer items-center gap-1.5 py-2 text-lg font-medium text-paper-900 underline underline-offset-[5px] hover:text-sage-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700"
          :aria-label="$t('pages.home.wizard.backAria')"
          @click="goBackTo(step - 1)"
        >
          <UIcon
            name="i-lucide-arrow-left"
            class="size-5 flex-none"
            aria-hidden="true"
          />
          {{ $t('pages.home.wizard.backShort') }}
        </button>
      </template>
    </HoliHomeSearchWizardProgress>

    <!-- Colonne gauche ≥ md : question, réponses, suggestions -->
    <div
      class="flex min-w-0 flex-col gap-6 max-md:[&_:is(a,button,input,label)]:scroll-mb-28 md:col-start-1 md:row-span-3 md:row-start-1 md:gap-7"
      data-wizard-main
    >
      <!-- Question + panneau. Seul ce bloc glisse entre étapes (ni la barre ni les actions). -->
      <Transition
        :name="stepTransition || 'wizard-step-none'"
        :css="stepTransition !== ''"
        @before-enter="onStepBeforeEnter"
        @after-enter="onStepEnterDone"
        @enter-cancelled="onStepEnterDone"
        @leave="onStepLeave"
      >
        <div
          :key="step"
          :data-step-transition="stepTransition || 'none'"
          class="flex min-w-0 flex-col gap-6 md:gap-7"
        >
          <div class="flex flex-col gap-2.5">
            <h2
              :id="questionId"
              ref="questionRef"
              tabindex="-1"
              class="m-0 font-display text-[30px] font-normal leading-[1.1] tracking-[-0.01em] text-paper-900 break-words focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sage-700 md:text-4xl"
            >
              {{ question }}
            </h2>
            <p
              :id="hintId"
              class="m-0 text-[17px] leading-[1.45] text-paper-900/75"
            >
              {{ hint }}
            </p>
          </div>

          <!-- Étape 1 · Type de séjour -->
          <div v-if="step === 1">
            <fieldset
              class="m-0 min-w-0 border-0 p-0"
              :aria-labelledby="questionId"
              :aria-describedby="hintId"
            >
              <div
                class="grid grid-cols-1 gap-3"
              >
                <label
                  v-for="opt in typeOptions"
                  :key="opt.value"
                  class="flex min-h-[52px] min-w-0 cursor-pointer flex-col gap-2.5 rounded-lg border-[1.5px] px-[18px] pt-[18px] pb-[22px] has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-sage-700"
                  :class="opt.checked ? TYPE_SELECTED_CLASS[opt.value] : 'border-sage-500 bg-paper-100 text-paper-900 hover:bg-paper-200/60'"
                >
                  <!-- `name="type"` : sans JS (ou avant hydratation), l'envoi natif du
                       formulaire recharge l'accueil en `?type=…`, la carte reste cochée. -->
                  <input
                    type="radio"
                    class="sr-only"
                    name="type"
                    :value="opt.value"
                    :checked="opt.checked"
                    @change="selectType(opt.value)"
                  >
                  <span class="flex w-full items-start justify-between gap-2">
                    <span class="min-w-0 text-[21px] font-medium leading-[1.2] break-words">{{ opt.title }}</span>
                    <span
                      aria-hidden="true"
                      class="flex size-[30px] flex-none items-center justify-center rounded-md border-[1.5px]"
                      :class="opt.checked ? 'border-current bg-current' : 'border-paper-900/75 bg-transparent'"
                    >
                      <svg
                        v-if="opt.checked"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        :class="TYPE_TICK_CLASS[opt.value]"
                      ><path d="M20 6 9 17l-5-5" /></svg>
                    </span>
                  </span>
                  <span
                    class="text-[17px] leading-[1.45]"
                    :class="opt.checked ? '' : 'text-paper-900/75'"
                  >{{ opt.description }}</span>
                </label>
              </div>
            </fieldset>
          </div>

          <!-- Étape 2 · Destination -->
          <div
            v-else-if="step === 2"
            class="flex min-w-0 flex-col gap-4"
          >
            <HoliSearchAutocomplete
              ref="autocompleteRef"
              appearance="wizard"
              :model-value="query"
              :placeholder="$t('pages.home.wizard.destination.placeholder')"
              :aria-label="$t('pages.home.wizard.destination.inputLabel')"
              :suggestions-label="$t('pages.home.wizard.destination.suggestionsLabel')"
              :count-type="selectedType ?? 'all'"
              :empty-message="noMatchMessage"
              @update:model-value="onQueryInput"
              @select="selectSuggestion"
            />

            <div aria-live="polite">
              <p
                v-if="place"
                class="m-0 flex items-center gap-2.5 text-[17px] text-type-cdh-ink"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  class="flex-none"
                ><path d="M20 6 9 17l-5-5" /></svg>
                <span class="min-w-0 break-words">{{ placeLine }}</span>
              </p>
            </div>

            <fieldset class="m-0 mt-1.5 min-w-0 border-0 p-0">
              <legend class="mb-4 p-0 text-[17px] text-paper-900/75">
                {{ $t('pages.home.wizard.destination.regionsLegend') }}
              </legend>
              <div class="flex flex-wrap gap-2.5">
                <label
                  v-for="chip in regionChips"
                  :key="chip.id"
                  class="inline-flex min-h-[52px] max-w-full cursor-pointer items-center rounded-lg border-[1.5px] px-5 py-2 text-center text-lg font-medium leading-tight [overflow-wrap:anywhere] has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-sage-700"
                  :class="isChipChecked(chip.place)
                    ? 'border-sage-700 bg-sage-700 text-paper-50'
                    : 'border-sage-500 bg-paper-100 text-paper-900 hover:bg-paper-200/60'"
                >
                  <input
                    type="radio"
                    class="sr-only"
                    name="home-wizard-region"
                    :value="chip.id"
                    :checked="isChipChecked(chip.place)"
                    @change="selectChip(chip.place)"
                  >
                  {{ chip.place.value }}
                </label>
              </div>
            </fieldset>
          </div>

          <!-- Étape 3 · Dates -->
          <div
            v-else-if="step === 3"
            class="flex min-w-0 flex-col gap-5"
          >
            <fieldset
              class="m-0 min-w-0 border-0 p-0"
              :aria-labelledby="questionId"
              :aria-describedby="hintId"
            >
              <div
                class="grid grid-cols-1 gap-3"
              >
                <label
                  v-for="opt in whenOptions"
                  :key="opt.value"
                  class="flex min-h-[60px] min-w-0 cursor-pointer items-center justify-between gap-3 rounded-lg border-[1.5px] px-[18px] py-3 text-xl font-medium leading-[1.2] has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-sage-700"
                  :class="opt.checked
                    ? 'border-sage-700 bg-sage-700 text-paper-50'
                    : 'border-sage-500 bg-paper-100 text-paper-900 hover:bg-paper-200/60'"
                >
                  <input
                    type="radio"
                    class="sr-only"
                    name="home-wizard-dates"
                    :value="opt.value"
                    :checked="opt.checked"
                    @change="selectWhen(opt.value)"
                  >
                  <span class="min-w-0 break-words">{{ opt.label }}</span>
                  <span
                    aria-hidden="true"
                    class="flex size-7 flex-none items-center justify-center rounded-md border-[1.5px]"
                    :class="opt.checked ? 'border-current bg-current' : 'border-paper-900/75 bg-transparent'"
                  >
                    <svg
                      v-if="opt.checked"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="text-sage-700"
                    ><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                </label>
              </div>
            </fieldset>

            <div aria-live="polite">
              <p
                v-if="when === 'weekend'"
                data-dates-text="weekend"
                class="m-0 text-[19px] leading-[1.45] text-paper-900"
              >
                {{ weekendText }}
              </p>
              <p
                v-else-if="when === 'unknown'"
                data-dates-text="unknown"
                class="m-0 text-[19px] leading-[1.45] text-paper-900"
              >
                {{ $t('pages.home.wizard.dates.unknownText') }}
              </p>
            </div>

            <HoliHomeSearchWizardCalendar
              v-if="when === 'pick'"
              :model-value="picked"
              :today="todayIso"
              single-month
              @update:model-value="onPickedUpdate"
            />
          </div>

          <!-- Étape 4 · Voyageurs -->
          <div
            v-else-if="step === 4"
            class="flex min-w-0 flex-col"
          >
            <div class="flex min-w-0 flex-col divide-y divide-paper-300 border-y border-paper-300">
              <div
                v-for="row in guestRows"
                :key="row.key"
                :data-guest-row="row.key"
                class="flex min-w-0 flex-wrap items-center justify-between gap-x-6 gap-y-3 py-4"
              >
                <div class="min-w-0">
                  <p class="m-0 text-[21px] font-medium leading-[1.2] break-words">
                    {{ row.label }}
                  </p>
                  <p class="m-0 text-[17px] leading-[1.45] text-paper-900/75 break-words">
                    {{ row.sublabel }}
                  </p>
                </div>
                <HoliGuestCounter
                  size="lg"
                  :model-value="row.value"
                  :min="row.min"
                  :max="row.max"
                  :input-label="row.inputLabel"
                  :decrement-label="row.decrementLabel"
                  :increment-label="row.incrementLabel"
                  @update:model-value="row.set"
                />
              </div>
            </div>
            <p
              v-if="children > 0"
              data-guests-ages-note
              class="m-0 pt-4 text-[17px] leading-[1.45] text-paper-900"
            >
              {{ $t('pages.home.wizard.guests.agesNote') }}
            </p>
          </div>

          <!-- Étape 5 · Envies (facultative) -->
          <div
            v-else
            data-envies-step
            class="flex min-w-0 flex-col"
          >
            <HoliSejourChips
              :model-value="sejours"
              :type="sejoursType"
              :labelledby="questionId"
              :describedby="hintId"
              @update:model-value="onSejoursUpdate"
            />
          </div>
        </div>
      </Transition>

      <!-- Zéro aux étapes 4-5 : actions pour élargir la recherche, en encart sous les réponses -->
      <div
        v-if="launchBlocked"
        ref="zeroActionsRef"
        data-zero-actions
        class="flex min-w-0 flex-col gap-3 rounded-lg border-[1.5px] border-paper-300 bg-paper-100 p-5"
      >
        <p class="m-0 text-[17px] leading-[1.45] text-paper-900">
          {{ $t('pages.home.wizard.zero.intro') }}
        </p>
        <div class="flex flex-wrap gap-2.5">
          <button
            v-if="sejours.length > 0"
            type="button"
            data-zero-action="remove-envies"
            :class="ZERO_ACTION_CLASS"
            @click="removeEnvies"
          >
            {{ $t('pages.home.wizard.zero.removeEnvies') }}
          </button>
          <button
            v-if="when !== 'unknown'"
            type="button"
            data-zero-action="free-dates"
            :class="ZERO_ACTION_CLASS"
            @click="freeDates"
          >
            {{ $t('pages.home.wizard.zero.freeDates') }}
          </button>
          <button
            ref="otherRegionRef"
            type="button"
            data-zero-action="other-region"
            :class="ZERO_ACTION_CLASS"
            @click="chooseOtherRegion"
          >
            {{ $t('pages.home.wizard.zero.otherRegion') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bouton principal. < md : collé en bas tant que sa place est sous l'écran
         (sticky dans le formulaire : il se décolle à la fin du parcours), sauf
         champ texte focalisé. ≥ md : en tête des actions de la colonne droite. -->
    <div
      class="flex min-w-0 flex-col max-md:-mx-4 max-md:-my-3 max-md:bg-paper-50 max-md:px-4 max-md:pt-3 max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))] md:col-start-2 md:row-start-2 md:self-start"
      :class="textFieldFocused ? '' : 'max-md:sticky max-md:bottom-0 max-md:z-10'"
      :data-bottom-bar="submitStuck && !textFieldFocused ? '' : undefined"
      data-wizard-submit
    >
      <!-- Étapes 4-5 à zéro : seule exception au « jamais grisé », accompagnée d'actions. -->
      <button
          ref="submitRef"
          type="submit"
          class="inline-flex min-h-14 items-center justify-center gap-2.5 rounded-lg px-7 py-3 text-center text-lg font-medium leading-tight break-words focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700"
          :class="launchBlocked
            ? 'cursor-default border-[1.5px] border-paper-300 bg-paper-200 text-paper-900'
            : 'cursor-pointer bg-sage-700 text-paper-50 hover:bg-sage-800'"
          :disabled="launchBlocked"
          :data-zero="launchBlocked ? '' : undefined"
          :aria-describedby="error ? errorId : undefined"
        >
          {{ step >= 4 ? launchLabel : $t('pages.home.wizard.continue') }}
          <UIcon
            v-if="!launchBlocked"
            name="i-lucide-arrow-right"
            class="size-[22px] flex-none"
            aria-hidden="true"
          />
        </button>
      <!-- Sous le bouton, dans le bloc collé : un refus reste visible même bouton collé. -->
      <p
        :id="errorId"
        aria-live="polite"
        class="m-0 min-h-1 text-[17px] text-red-700 [&:not(:empty)]:mt-2"
      >
        {{ error }}
      </p>
    </div>

    <!-- Actions secondaires + message de validation (écart de 12 px avec le bouton principal) -->
    <div
      ref="actionsRef"
      class="flex min-w-0 flex-col gap-3 max-md:-mt-3 md:col-start-2 md:row-start-3 md:-mt-4 md:self-start"
      data-wizard-actions
    >
      <div class="flex flex-col items-stretch gap-3">
        <!-- Étape 4 : ouvre l'étape 5, jamais imposée. < md : lien souligné, plus compact. -->
        <button
          v-if="step === 4"
          type="button"
          data-open-envies
          class="inline-flex min-h-11 cursor-pointer items-center self-start py-2 text-left text-lg font-medium text-sage-700 underline underline-offset-[5px] break-words max-md:hover:text-sage-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 md:min-h-14 md:self-auto md:justify-center md:rounded-lg md:border-2 md:border-sage-700 md:bg-transparent md:px-7 md:py-3 md:text-center md:leading-tight md:no-underline md:hover:bg-sage-100"
          @click="openEnvies"
        >
          {{ $t('pages.home.wizard.envies.open') }}
        </button>
        <!-- Sortie anticipée (étapes 2-3, lieu retenu) : dates libres, 2 adultes. -->
        <button
          v-if="showEarlyExit"
          type="button"
          data-early-exit
          class="min-h-11 cursor-pointer self-start py-2 text-left text-lg text-sage-700 underline underline-offset-[5px] break-words hover:text-sage-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 md:self-auto"
          @click="launchEarly"
        >
          {{ earlyLabel }}
        </button>
        <button
          v-if="step > 1"
          type="button"
          class="max-md:hidden min-h-11 cursor-pointer self-start py-2 text-lg font-medium text-paper-900 underline underline-offset-[5px] hover:text-sage-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 md:self-auto"
          @click="goBackTo(step - 1)"
        >
          {{ $t('pages.home.wizard.back') }}
        </button>
      </div>


      <!-- Annonce du libellé chiffré, une fois par changement d'étape -->
      <p
        aria-live="polite"
        data-count-live
        class="sr-only"
      >
        {{ liveMessage }}
      </p>
    </div>
  </form>
</template>

<style scoped>
/* Entrée d'une étape : glissement de 28 px + fondu, 0,32 s, depuis la droite en
 * avançant, depuis la gauche en revenant. Seule l'entrée a une durée : l'étape
 * quittée disparaît aussitôt. Sous `prefers-reduced-motion: reduce`, aucune
 * règle ne s'applique : ni glissement ni fondu (`.slide-x` de main.css garde un
 * fondu, d'où des classes propres). */
@media (prefers-reduced-motion: no-preference) {
  .wizard-step-forward-enter-active,
  .wizard-step-back-enter-active {
    transition: transform 0.32s cubic-bezier(0.2, 0.7, 0.2, 1), opacity 0.32s ease-out;
  }

  .wizard-step-forward-enter-from {
    transform: translateX(28px);
    opacity: 0;
  }

  .wizard-step-back-enter-from {
    transform: translateX(-28px);
    opacity: 0;
  }
}
</style>
